import React, { memo, useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { connect } from 'react-redux';

import config from '../config';
import { showError } from '../utils/MessagePopup';
import { isResearcher } from '../utils/checkPermissions';
import { CategoryManagement } from '../components/CategoryManagement';
import {
  getManagementAgreementCategories,
  sortAgreementCategories,
  saveAgreementCategory,
  deleteAgreementCategory,
} from '../actions/agreementCategories';
import confirm from './decorators/confirm';
import { usePopupContext } from '../contexts/PopupContext';

const INITIAL_FORM_STATE = {
  mode: 'view',
  id: '',
  category: '',
  parentId: '',
  errors: [],
  showParent: false,
  isLinkable: false,
};

const CategoriesManagementContainer = ({
  user,
  history,
  loading,
  agreementCategories,
  sort,
  parents,
  getManagementAgreementCategories,
  sortAgreementCategories,
  saveAgreementCategory,
  deleteAgreementCategory,
}) => {
  const popupContext = usePopupContext();
  const { openPopup, closePopup } = popupContext;
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const { mode, id, category, parentId, errors, showParent, isLinkable } = formState;

  const resetState = useCallback(() => setFormState(INITIAL_FORM_STATE), []);

  const handleDeleteEvent = useCallback(
    (event, data) => {
      deleteAgreementCategory({
        id: data.id,
        afterSuccess: () => {
          getManagementAgreementCategories();
        },
        afterError: errorPayload => {
          try {
            setFormState(prev => ({
              ...prev,
              errors: Object.entries(errorPayload.responseBody.errors).map(([value]) => value),
            }));
          } catch (error) {
            setFormState(prev => ({ ...prev, errors: ['Unknown error'] }));
          }
        },
      });
      closePopup();
    },
    [deleteAgreementCategory, closePopup],
  );

  const handleDelete = useCallback(
    (...args) => {
      confirm('Delete this category?', handleDeleteEvent, popupContext)(...args);
    },
    [handleDeleteEvent, popupContext],
  );

  const handleEdit = useCallback((event, data) => {
    setFormState({
      mode: 'edit',
      id: data.id,
      category: data.category,
      parentId: data.parentId,
      isLinkable: data.isLinkable,
      errors: [],
      showParent: data.parentId !== '',
    });
  }, []);

  const handleAdd = useCallback(() => {
    setFormState({
      mode: 'add',
      id: '',
      category: '',
      parentId: '',
      errors: [],
      showParent: true,
      isLinkable: false,
    });
  }, []);

  const handleCancel = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleSave = useCallback(() => {
    const validationErrors = [];

    if (!category) {
      validationErrors.push('Category name can not be empty');
    }

    if (validationErrors.length > 0) {
      setFormState(prev => ({ ...prev, errors: validationErrors }));

      return;
    }

    const saveBody = {
      category,
      isLinkable,
      parentId: parentId || undefined,
      afterSuccess: () => {
        resetState();
        getManagementAgreementCategories();
      },
      afterError: errorPayload => {
        try {
          setFormState(prev => ({
            ...prev,
            errors: Object.entries(errorPayload.responseBody.errors).map(([value]) => value),
          }));
        } catch (error) {
          setFormState(prev => ({ ...prev, errors: ['Unknown error'] }));
        }
      },
    };

    if (mode === 'edit') {
      saveBody.id = id;
    }
    saveAgreementCategory(saveBody);
  }, [category, isLinkable, parentId, mode, id, resetState, saveAgreementCategory]);

  const handleChange = useCallback(({ target }) => {
    const { name, value, type } = target;

    setFormState(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? target.checked : value,
      errors: [],
    }));
  }, []);

  const handleClick = useCallback(
    name => {
      sortAgreementCategories(name);
    },
    [sortAgreementCategories],
  );

  useEffect(() => {
    const canAccess = user && !isResearcher(user.getIn(['roles', 0, 'slug'], null));

    if (!canAccess) {
      showError(openPopup, config.permisionError, () => {
        history.push('/');
        closePopup();
      });
    } else {
      getManagementAgreementCategories();
    }
  }, []);

  if (!user || isResearcher(user.getIn(['roles', 0, 'slug'], null))) return null;

  const slug = user.getIn(['roles', 0, 'slug'], null);

  return (
    <div className="full-height">
      <Helmet title="Categories Management Page" />
      <CategoryManagement
        agreementCategories={agreementCategories}
        category={category}
        errors={errors}
        id={id}
        isLinkable={isLinkable}
        isSuper={slug === config.ADMINISTRATOR}
        loading={loading}
        mode={mode}
        onAdd={handleAdd}
        onCancel={handleCancel}
        onChange={handleChange}
        onClick={handleClick}
        onDelete={handleDelete}
        onEdit={handleEdit}
        onSave={handleSave}
        parentId={parentId}
        parents={parents}
        showParent={showParent}
        sort={sort}
        user={user}
      />
    </div>
  );
};

const mapStateToProps = state => ({
  loading: state.agreementCategories.get('loading'),
  agreementCategories: state.agreementCategories.get('data'),
  sort: state.agreementCategories.get('sort'),
  parents: state.agreementCategories.get('parents'),
  user: state.auth.get('user', null),
});

export default connect(mapStateToProps, {
  getManagementAgreementCategories,
  sortAgreementCategories,
  saveAgreementCategory,
  deleteAgreementCategory,
})(memo(CategoriesManagementContainer));
